/**
 * DueBadge — the urgency marker on a draft whose customer-requested ship or
 * pickup date is due (kanban 3d77f755, Lakewood PO 6037455).
 *
 * Reads the server-computed `due_urgency` on a /portal/orders row (the
 * list and the order modal; services/orders/draft_due.py decides on the
 * desk's calendar in business days). Renders nothing when the row is not
 * at risk. A will-call row says PICKUP because a missed date there is a
 * wasted trip, not a late carrier. Colours live in DueBadge.module.css:
 * one red at three intensities, neutral once the desk has answered.
 */
import styles from './DueBadge.module.css';
import { dueBadgeLabel, dueBadgeTitle, dueTone, type DueOrder } from './dueBadgeText';

interface DueBadgeProps {
  order: DueOrder;
}

function ClockIcon() {
  return (
    <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.2 2" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

export default function DueBadge({ order }: DueBadgeProps) {
  const label = dueBadgeLabel(order);
  const tone = dueTone(order);
  if (!label || !order.due_urgency || !tone) return null;
  return (
    <span
      data-testid={`due-badge-${order.id}`}
      data-urgency={order.due_urgency}
      data-desk-state={order.due_desk_state ?? undefined}
      data-tone={tone}
      title={dueBadgeTitle(order)}
      className={`${styles.badge} ${styles[tone]}`}
    >
      {tone === 'handled' ? <CheckIcon /> : <ClockIcon />}
      {label}
    </span>
  );
}
